import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { AuthContext } from '../App';
import { Shield, ArrowLeft, Settings, Users, List, DollarSign, TrendingUp, CreditCard, MessageSquare } from 'lucide-react';
import EnumManager from '../components/admin/EnumManager';
import UserManagement from '../components/admin/UserManagement';
import SystemConfig from '../components/admin/SystemConfig';
import DocumentVerification from '../components/admin/DocumentVerification';
import FeedbackManagement from '../components/admin/FeedbackManagement';
import axios from 'axios';
import { toast } from 'sonner';
import Footer from '../components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api` || 'http://localhost:8001/api';

export default function AdminPanel() {
  const { user } = React.useContext(AuthContext);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('documents');
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch admin stats:', error);
      toast.error('Failed to load statistics');
    } finally {
      setLoadingStats(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900" data-testid="admin-panel">
      {/* Navbar */}
      <nav className="glass border-b dark:border-gray-700">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <img src="/assets/refrx-logo-black.png" alt="RefrX" className="h-10 w-auto" />
              <span className="text-gray-400">|</span>
              <span className="font-bold text-xl">Admin Panel</span>
            </div>
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total Registered Users</CardTitle>
              <Users className="w-5 h-5 text-blue-100" />
            </CardHeader>
            <CardContent>
              {loadingStats ? (
                <div className="text-3xl font-bold">Loading...</div>
              ) : (
                <>
                  <div className="text-4xl font-bold">{stats?.total_users || 0}</div>
                  <div className="mt-2 flex gap-4 text-sm text-blue-100">
                    <span>Candidates: {stats?.users_by_role?.candidates || 0}</span>
                    <span>Recruiters: {stats?.users_by_role?.recruiters || 0}</span>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
              <DollarSign className="w-5 h-5 text-green-100" />
            </CardHeader>
            <CardContent>
              {loadingStats ? (
                <div className="text-3xl font-bold">Loading...</div>
              ) : (
                <>
                  <div className="text-4xl font-bold">
                    ${stats?.revenue?.total_revenue?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                  </div>
                  <p className="text-sm text-green-100 mt-2">
                    From credit purchases
                  </p>
                </>
              )}
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Credits Sold</CardTitle>
              <CreditCard className="w-5 h-5 text-purple-100" />
            </CardHeader>
            <CardContent>
              {loadingStats ? (
                <div className="text-3xl font-bold">Loading...</div>
              ) : (
                <>
                  <div className="text-4xl font-bold">{stats?.revenue?.total_credits_sold?.toLocaleString() || 0}</div>
                  <p className="text-sm text-purple-100 mt-2">
                    {stats?.revenue?.total_transactions || 0} transactions
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 max-w-3xl mx-auto">
            <TabsTrigger value="documents" className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Documents
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Users
            </TabsTrigger>
            <TabsTrigger value="enums" className="flex items-center gap-2">
              <List className="w-4 h-4" />
              Enums
            </TabsTrigger>
            <TabsTrigger value="config" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Config
            </TabsTrigger>
            <TabsTrigger value="feedback" className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Feedback
            </TabsTrigger>
          </TabsList>

          <TabsContent value="documents">
            <DocumentVerification />
          </TabsContent>

          <TabsContent value="users">
            <UserManagement />
          </TabsContent>

          <TabsContent value="enums">
            <EnumManager />
          </TabsContent>

          <TabsContent value="config">
            <SystemConfig />
          </TabsContent>

          <TabsContent value="feedback">
            <FeedbackManagement />
          </TabsContent>
        </Tabs>
      </div>
      <Footer />
    </div>
  );
}